/**
 * NetworkTopologyPage.tsx — v2
 *
 * Fix summary vs v1:
 *  - TOOLTIP: removed the pointer-events overlay that was intercepting SVG events and
 *    causing flicker. Mouse position is tracked via a ref on the outer container div
 *    and the tooltip DOM element is repositioned via direct style mutation (no setState
 *    on every mousemove → no re-renders → no flicker).
 *  - CLICK vs DRAG: replaced competing onMouseDown/onClick with a drag-distance
 *    threshold (hasMoved ref). A mousedown+mouseup without ≥4px movement is a click.
 *  - PANEL: PropertiesPanel now has pointer-events intercepted cleanly (stopPropagation
 *    on panel mousedown), so clicking inside the panel doesn't toggle node selection.
 *  - FILTERS: port filter + building filter pill-bar; filtered subgraph auto-fits view.
 *  - LOD: when scale < 0.35, ONT nodes are hidden and buildings show a count badge
 *    so the canvas stays readable at wide zoom levels.
 *  - PERF: nodes and edges memoised; TopologyNode wrapped in React.memo with stable
 *    callbacks so only changed nodes re-render on drag.
 */

import React, {
  useState, useEffect, useRef, useCallback, useMemo, memo
} from 'react'
import {
  Server, Wifi, Building2, Zap, X, Save,
  ZoomIn, ZoomOut, Maximize2, Minimize2, Info, AlertTriangle, CheckCircle,
  Activity, Database, Cable, Layers, Filter, RotateCcw
} from 'lucide-react'
import customersJson from '@/constants/customers.json'

// ─── Types ───────────────────────────────────────────────────────────────────

type NodeType = 'olt' | 'splitter' | 'building' | 'ont'

interface TopoNode {
  id: string
  type: NodeType
  label: string
  x: number
  y: number
  oltIp?: string
  txPower?: number
  ratio?: number
  port?: number
  building?: string
  portNum?: number
  customerName?: string
  mobile?: string
  serviceNumber?: string
  bbUserId?: string
  vlan?: number
  subscriptionPlan?: string
  fmc?: number
  rxPower?: number
  pathLoss?: number
  _custCount?: number
  bSplitterRatio?: number  // secondary splitter at building distribution point
}

interface TopoEdge {
  id: string
  from: string
  to: string
  lengthKm?: number
  label?: string
  edgeLoss?: number   // pre-computed cable attenuation for this segment (dB)
}

interface CustomerRecord {
  customerName?: string
  mobile?: string
  serviceNumber?: string
  bbUserId?: string
  oltIp?: string
  vlan?: number | null
  port?: number | null
  building?: string
  subscriptionPlan?: string
  fmc?: number
}

// ─── Optical power helpers ────────────────────────────────────────────────────

const SPLITTER_LOSS: Record<number, number> = { 2:3.8, 4:7.2, 8:10.7, 16:13.8, 32:17.0, 64:20.0 }
const FIBER_DB_PER_KM = 0.35
const CONNECTOR_LOSS = 0.3
const ONT_MIN_RX  = -28
const ONT_WARN_RX = -24

function splitterLoss(ratio: number) { return SPLITTER_LOSS[ratio] ?? (10 * Math.log10(ratio) + 3.5) }
function cableLoss(km: number) { return km * FIBER_DB_PER_KM + CONNECTOR_LOSS }
function powerColor(dbm?: number) {
  if (dbm === undefined) return '#64748b'
  if (dbm >= ONT_WARN_RX) return '#059669'
  if (dbm >= ONT_MIN_RX)  return '#d97706'
  return '#dc2626'
}
function powerLabel(dbm?: number) { return dbm === undefined ? '—' : `${dbm.toFixed(1)} dBm` }

// ─── Topology builder ─────────────────────────────────────────────────────────

function buildTopology(customers: CustomerRecord[], oltTxPower: number) {
  const nodes: TopoNode[] = []
  const edges: TopoEdge[] = []

  const oltId = 'olt-0'
  nodes.push({ id: oltId, type: 'olt', label: 'OLT', x: 0, y: 0,
    oltIp: '10.215.181.225', txPower: oltTxPower, rxPower: oltTxPower })

  const byPort: Record<number, CustomerRecord[]> = {}
  customers.forEach(c => { const p = c.port ?? 0; (byPort[p] ??= []).push(c) })
  const ports = Object.keys(byPort).map(Number).sort((a, b) => a - b)

  ports.forEach((port, pi) => {
    const portCustomers = byPort[port]
    const pSplId = `sp-port-${port}`
    const pSplRatio = 8
    const feederKm = 1.0
    const feederLoss = cableLoss(feederKm)
    const pSplLoss  = splitterLoss(pSplRatio)
    const pSplRx    = oltTxPower - feederLoss - pSplLoss
    const pSplX     = (pi - (ports.length - 1) / 2) * 520

    nodes.push({ id: pSplId, type: 'splitter',
      label: `Port ${port || '—'}\n1:${pSplRatio} Splitter`,
      x: pSplX, y: 190, ratio: pSplRatio, port: port || undefined,
      rxPower: pSplRx, pathLoss: feederLoss + pSplLoss })
    edges.push({ id: `e-olt-${pSplId}`, from: oltId, to: pSplId,
      lengthKm: feederKm, label: `${feederKm} km feeder`, edgeLoss: feederLoss })

    const byBuilding: Record<string, CustomerRecord[]> = {}
    portCustomers.forEach(c => { const b = c.building || 'Unknown'; (byBuilding[b] ??= []).push(c) })
    const buildings = Object.keys(byBuilding).sort()

    buildings.forEach((bldg, bi) => {
      const bldgCustomers = byBuilding[bldg]
      const bRatio = bldgCustomers.length <= 4 ? 4 : bldgCustomers.length <= 8 ? 8 : 16
      const bLoss  = splitterLoss(bRatio)
      const distKm = 0.4
      const distLoss = cableLoss(distKm)
      const prevLoss = oltTxPower - pSplRx
      const bRx = oltTxPower - prevLoss - distLoss - bLoss

      const bx = pSplX + (bi - (buildings.length - 1) / 2) * 210
      const by = 390
      const bldgId = `bldg-${port}-${bldg.replace(/\s+/g,'_')}`

      nodes.push({ id: bldgId, type: 'building', label: bldg,
        x: bx, y: by, building: bldg, port: port || undefined,
        rxPower: bRx, pathLoss: prevLoss + distLoss + bLoss,
        _custCount: bldgCustomers.length, bSplitterRatio: bRatio })
      edges.push({ id: `e-${pSplId}-${bldgId}`, from: pSplId, to: bldgId,
        lengthKm: distKm, label: `${distKm} km`, edgeLoss: distLoss })

      bldgCustomers.forEach((cust, ci) => {
        const ontId = `ont-${cust.serviceNumber ?? `${port}-${bldg}-${ci}`}`
        const dropKm = 0.08
        const dropLoss = cableLoss(dropKm)
        const ontRx = bRx - dropLoss

        nodes.push({
          id: ontId, type: 'ont',
          label: (cust.customerName ?? 'ONT').split(' ').slice(0, 2).join(' '),
          x: bx + (ci - (bldgCustomers.length - 1) / 2) * 130,
          y: by + 210,
          customerName: cust.customerName, mobile: cust.mobile,
          serviceNumber: cust.serviceNumber, bbUserId: cust.bbUserId,
          vlan: cust.vlan ?? undefined, portNum: cust.port ?? undefined,
          subscriptionPlan: cust.subscriptionPlan, fmc: cust.fmc,
          building: bldg, rxPower: ontRx, pathLoss: prevLoss + distLoss + bLoss + dropLoss
        })
        edges.push({ id: `e-${bldgId}-${ontId}`, from: bldgId, to: ontId,
          label: `VLAN ${cust.vlan ?? '—'}`, edgeLoss: dropLoss })
      })
    })
  })

  return { nodes, edges }
}

// ─── Recalculate all optical power values by BFS from OLT ─────────────────────
// Keeps existing node positions and ratios — only updates rxPower & pathLoss.

function recalculatePowers(nodes: TopoNode[], edges: TopoEdge[], oltTxPower: number): TopoNode[] {
  const nodeMap: Record<string, TopoNode> = {}
  nodes.forEach(n => { nodeMap[n.id] = n })

  // adjacency list: parent → [child ids]
  const adj: Record<string, string[]> = {}
  const edgeByTo: Record<string, TopoEdge> = {}
  edges.forEach(e => { (adj[e.from] ??= []).push(e.to); edgeByTo[e.to] = e })

  const rxMap:       Record<string, number> = {}
  const pathLossMap: Record<string, number> = {}

  const olt = nodes.find(n => n.type === 'olt')
  if (!olt) return nodes
  rxMap[olt.id]       = oltTxPower
  pathLossMap[olt.id] = 0

  const queue = [olt.id]
  while (queue.length) {
    const pid = queue.shift()!
    for (const cid of (adj[pid] ?? [])) {
      const child = nodeMap[cid]
      const edge  = edgeByTo[cid]
      if (!child || !edge) continue

      const cableLossSeg = edge.edgeLoss ?? 0
      let   nodeLoss = 0
      if (child.type === 'splitter') nodeLoss = splitterLoss(child.ratio ?? 8)
      if (child.type === 'building') nodeLoss = splitterLoss(child.bSplitterRatio ?? 8)
      // ONT: no splitter, just the drop cable (already in edgeLoss)

      const totalLoss = cableLossSeg + nodeLoss
      rxMap[cid]       = (rxMap[pid] ?? oltTxPower) - totalLoss
      pathLossMap[cid] = (pathLossMap[pid] ?? 0) + totalLoss

      queue.push(cid)
    }
  }

  return nodes.map(n => ({
    ...n,
    rxPower:  rxMap[n.id]       ?? n.rxPower,
    pathLoss: pathLossMap[n.id] ?? n.pathLoss,
    ...(n.type === 'olt' ? { txPower: oltTxPower, rxPower: oltTxPower } : {})
  }))
}

// ─── Calibration persistence (localStorage) ───────────────────────────────────

const CAL_KEY = 'bsnl-topology-calibration'

interface CalibrationData {
  oltTxPower:     number
  splitterRatios: Record<string, number>           // nodeId → ratio
  nodePositions:  Record<string, { x: number; y: number }>
  savedAt:        string
  savedLabel:     string
}

function saveCalibration(oltTxPower: number, nodes: TopoNode[]): string {
  const now = new Date()
  const label = now.toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })
  const cal: CalibrationData = {
    oltTxPower,
    splitterRatios: Object.fromEntries(
      nodes.filter(n => n.type === 'splitter').map(n => [n.id, n.ratio ?? 8])
    ),
    nodePositions: Object.fromEntries(
      nodes.map(n => [n.id, { x: n.x, y: n.y }])
    ),
    savedAt:    now.toISOString(),
    savedLabel: label
  }
  localStorage.setItem(CAL_KEY, JSON.stringify(cal))
  return label
}

function loadCalibration(): CalibrationData | null {
  try { const s = localStorage.getItem(CAL_KEY); return s ? JSON.parse(s) : null }
  catch { return null }
}

function applyCalibration(nodes: TopoNode[], cal: CalibrationData): TopoNode[] {
  return nodes.map(n => ({
    ...n,
    x: cal.nodePositions[n.id]?.x ?? n.x,
    y: cal.nodePositions[n.id]?.y ?? n.y,
    ...(n.type === 'splitter' && cal.splitterRatios[n.id] !== undefined
      ? { ratio: cal.splitterRatios[n.id] }
      : {})
  }))
}

// ─── Node sizes ───────────────────────────────────────────────────────────────

const NS: Record<NodeType, { w: number; h: number }> = {
  olt:      { w: 124, h: 60 },
  splitter: { w: 112, h: 54 },
  building: { w: 114, h: 52 },
  ont:      { w: 102, h: 46 },
}

// ─── SVG Node — memo'd to prevent full re-render on every frame ───────────────

interface NodeProps {
  node: TopoNode
  selected: boolean
  hovered: boolean
  onPointerDown: (e: React.PointerEvent) => void
  onPointerEnter: (e: React.PointerEvent) => void
  onPointerLeave: (e: React.PointerEvent) => void
}

const TopologyNode = memo(function TopologyNode({
  node, selected, hovered, onPointerDown, onPointerEnter, onPointerLeave
}: NodeProps) {
  const { w, h } = NS[node.type]
  const pColor = powerColor(node.rxPower)
  const isWeak = node.rxPower !== undefined && node.rxPower < ONT_MIN_RX
  const isWarn = node.rxPower !== undefined && node.rxPower >= ONT_MIN_RX && node.rxPower < ONT_WARN_RX

  let nodeFill = ''
  let subtextColor = ''
  
  if (node.type === 'olt') {
    nodeFill = 'url(#oltGrad)'
    subtextColor = '#93c5fd'
  } else if (node.type === 'splitter') {
    nodeFill = 'url(#splitterGrad)'
    subtextColor = '#ddd6fe'
  } else if (node.type === 'building') {
    nodeFill = 'url(#buildingGrad)'
    subtextColor = '#cffafe'
  } else {
    // ONT
    if (isWeak) {
      nodeFill = 'url(#ontWeakGrad)'
      subtextColor = '#fecaca'
    } else if (isWarn) {
      nodeFill = 'url(#ontWarnGrad)'
      subtextColor = '#fde68a'
    } else if (node.rxPower === undefined) {
      nodeFill = 'url(#ontOfflineGrad)'
      subtextColor = '#cbd5e1'
    } else {
      nodeFill = 'url(#ontGoodGrad)'
      subtextColor = '#a7f3d0'
    }
  }

  const defaultBorders: Record<NodeType, string> = {
    olt:      '#3b82f6',
    splitter: '#a855f7',
    building: '#06b6d4',
    ont:      isWeak ? '#ef4444' : isWarn ? '#f59e0b' : '#10b981',
  }

  const stroke = selected ? '#ffffff' : hovered ? '#ffffff' : defaultBorders[node.type]
  const sw     = selected ? 3.5 : hovered ? 2 : 1
  const shadow = selected
    ? '0 0 16px rgba(255,255,255,0.7), 0 8px 24px rgba(0,0,0,0.3)'
    : hovered
    ? '0 0 10px rgba(255,255,255,0.4), 0 6px 14px rgba(0,0,0,0.2)'
    : '0 4px 10px rgba(0,0,0,0.1)'

  return (
    <g
      transform={`translate(${node.x},${node.y})`}
      style={{ cursor: 'pointer' }}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {/* Card background */}
      <rect
        x={-w/2} y={-h/2} width={w} height={h}
        rx={node.type === 'ont' ? 9 : 13}
        fill={nodeFill}
        stroke={stroke} strokeWidth={sw}
        style={{ filter: `drop-shadow(${shadow})`, transition: 'stroke 0.15s, stroke-width 0.15s' }}
      />

      {/* Power indicator bar — left edge */}
      {node.type !== 'olt' && (
        <rect x={-w/2} y={-h/2+4} width={3} height={h-8} rx={2} fill={pColor} opacity={0.95} />
      )}

      {/* Alert dot */}
      {(isWeak || isWarn) && (
        <circle cx={w/2-9} cy={-h/2+9} r={4.5} fill="#ffffff" opacity={0.9}>
          <animate attributeName="opacity" values="0.9;0.2;0.9" dur="1.2s" repeatCount="indefinite" />
        </circle>
      )}

      {/* ── OLT ── */}
      {node.type === 'olt' && (<>
        <Server x={-w/2+10} y={-10} size={18} className="text-blue-100" />
        <text x={-w/2+33} y={-2} fontSize={11} fontWeight="800" fill="#ffffff" fontFamily="Inter,system-ui">OLT</text>
        <text x={-w/2+33} y={8}  fontSize={8}  fill={subtextColor} fontFamily="monospace" fontWeight="600">{node.oltIp}</text>
        <text x={-w/2+10}  y={h/2-6} fontSize={8} fill="#bfdbfe" fontWeight="800" fontFamily="Inter,system-ui">
          TX: {powerLabel(node.txPower)}
        </text>
      </>)}

      {/* ── Splitter ── */}
      {node.type === 'splitter' && (<>
        <text x={0} y={-8} fontSize={10} fontWeight="800" fill="#ffffff" textAnchor="middle" fontFamily="Inter,system-ui">
          {node.port !== undefined ? `Port ${node.port}` : 'Port —'}
        </text>
        <text x={0} y={5}  fontSize={8.5} fill={subtextColor} textAnchor="middle" fontFamily="Inter,system-ui" fontWeight="600">
          1:{node.ratio} Splitter
        </text>
        <text x={0} y={h/2-6} fontSize={8} fill="#ffffff" textAnchor="middle" fontWeight="850" fontFamily="Inter,system-ui">
          {powerLabel(node.rxPower)}
        </text>
      </>)}

      {/* ── Building ── */}
      {node.type === 'building' && (<>
        <Building2 x={-w/2+9} y={-10} size={17} className="text-cyan-100" />
        <text x={-w/2+30} y={-2} fontSize={9.5} fontWeight="800" fill="#ffffff" fontFamily="Inter,system-ui">
          {node.label.length > 12 ? node.label.slice(0,11)+'…' : node.label}
        </text>
        <text x={-w/2+30} y={8} fontSize={8} fill={subtextColor} fontFamily="Inter,system-ui" fontWeight="600">
          {node._custCount ?? 0} tenants
        </text>
        <text x={-w/2+9} y={h/2-6} fontSize={8} fill="#ffffff" fontWeight="850" fontFamily="Inter,system-ui">
          {powerLabel(node.rxPower)}
        </text>
      </>)}

      {/* ── ONT ── */}
      {node.type === 'ont' && (<>
        <Wifi x={-w/2+7} y={-9} size={13} color="#ffffff" />
        <text x={-w/2+24} y={-2} fontSize={8.5} fontWeight="800" fill="#ffffff" fontFamily="Inter,system-ui">
          {(node.label ?? '').length > 13 ? (node.label ?? '').slice(0,13)+'…' : (node.label ?? '')}
        </text>
        <text x={-w/2+24} y={8} fontSize={7.5} fill={subtextColor} fontWeight="850" fontFamily="Inter,system-ui">
          {powerLabel(node.rxPower)}
        </text>
        <text x={-w/2+7} y={h/2-5} fontSize={7} fill={subtextColor} fontFamily="monospace" fontWeight="600">
          VLAN {node.vlan ?? '—'}
        </text>
      </>)}
    </g>
  )
})

// ─── Edge ─────────────────────────────────────────────────────────────────────

const TopologyEdge = memo(function TopologyEdge({
  edge, fromNode, toNode, hovered
}: { edge: TopoEdge; fromNode: TopoNode; toNode: TopoNode; hovered: boolean }) {
  if (!fromNode || !toNode) return null
  const { h: fh } = NS[fromNode.type]
  const { h: th } = NS[toNode.type]
  const midY = (fromNode.y + toNode.y) / 2
  const d = `M ${fromNode.x} ${fromNode.y+fh/2} C ${fromNode.x} ${midY}, ${toNode.x} ${midY}, ${toNode.x} ${toNode.y-th/2}`
  const midX = (fromNode.x + toNode.x) / 2

  return (
    <g>
      {/* Wider invisible hit zone */}
      <path d={d} fill="none" stroke="transparent" strokeWidth={14} />
      <path d={d} fill="none"
        stroke={hovered ? '#3b82f6' : 'rgba(99,130,246,0.45)'}
        strokeWidth={hovered ? 2.5 : 1.5}
        strokeDasharray={toNode.type === 'ont' ? '5 3' : 'none'}
        style={{ transition: 'stroke 0.15s' }}
      />
      {hovered && (
        <circle r={3.5} fill="#3b82f6" opacity={0.9}>
          <animateMotion dur="0.9s" repeatCount="indefinite" path={d} />
        </circle>
      )}
      {edge.label && (
        <text x={midX} y={midY-5} fontSize={7.5} fill="#475569" textAnchor="middle" fontFamily="monospace" fontWeight="600">
          {edge.label}
        </text>
      )}
    </g>
  )
})

// ─── Tooltip — rendered as a portal, repositioned via ref (no setState flicker) ──

interface TooltipContent { node: TopoNode }

function NodeTooltip({ node }: TooltipContent) {
  const isWeak = node.rxPower !== undefined && node.rxPower < ONT_MIN_RX
  const isWarn = node.rxPower !== undefined && node.rxPower >= ONT_MIN_RX && node.rxPower < ONT_WARN_RX
  const sc = isWeak ? 'text-red-600' : isWarn ? 'text-amber-600' : 'text-emerald-600'
  const st = isWeak ? '⚠ Signal Weak' : isWarn ? 'Signal Low' : '✓ Good Signal'

  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-100"
      style={{ background:'rgba(255,255,255,0.98)', backdropFilter:'blur(16px)', minWidth:240, maxWidth:280, boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }}>
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-slate-100"
        style={{ background:'linear-gradient(135deg,rgba(59,130,246,0.06),rgba(6,182,212,0.03))' }}>
        <div className="flex items-center gap-2">
          {node.type === 'olt'      && <Server   size={13} className="text-blue-600 shrink-0" />}
          {node.type === 'splitter' && <Layers   size={13} className="text-purple-600 shrink-0" />}
          {node.type === 'building' && <Building2 size={13} className="text-cyan-600 shrink-0" />}
          {node.type === 'ont'      && <Wifi     size={13} className="text-emerald-600 shrink-0" />}
          <p className="text-slate-800 font-black text-sm truncate">
            {node.type === 'ont' ? node.customerName : node.label.replace(/\n/g,' ')}
          </p>
        </div>
        {node.rxPower !== undefined && (
          <div className={`flex items-center gap-1.5 mt-1 ${sc}`}>
            <Activity size={9} /><span className="text-[10px] font-bold">{st}</span>
          </div>
        )}
      </div>

      {/* Rows */}
      <div className="px-4 py-3 space-y-1.5 text-xs">
        {node.type === 'olt' && (<>
          <TRow l="OLT IP"      v={node.oltIp}                m />
          <TRow l="TX Power"    v={`${node.txPower} dBm`}     c="text-blue-600" />
          <TRow l="Standard"    v="GPON ITU-T G.984" />
          <TRow l="Wavelength"  v="1490 nm downstream" />
        </>)}
        {node.type === 'splitter' && (<>
          <TRow l="Port"        v={node.port !== undefined ? `Port ${node.port}` : '—'} />
          <TRow l="Split Ratio" v={`1 : ${node.ratio}`} />
          <TRow l="Split Loss"  v={`${splitterLoss(node.ratio!).toFixed(1)} dB`} c="text-purple-600" />
          <TRow l="RX Power"    v={powerLabel(node.rxPower)} c={powerColor(node.rxPower)} />
          <TRow l="Path Loss"   v={`${node.pathLoss?.toFixed(2)} dB`} />
        </>)}
        {node.type === 'building' && (<>
          <TRow l="Building"    v={node.building} />
          <TRow l="OLT Port"    v={node.port !== undefined ? `Port ${node.port}` : '—'} />
          <TRow l="Tenants"     v={String(node._custCount ?? 0)} />
          <TRow l="RX Power"    v={powerLabel(node.rxPower)} c={powerColor(node.rxPower)} />
          <TRow l="Path Loss"   v={`${node.pathLoss?.toFixed(2)} dB`} />
        </>)}
        {node.type === 'ont' && (<>
          <TRow l="Customer"    v={node.customerName} />
          <TRow l="Service No." v={node.serviceNumber} m />
          <TRow l="BB User ID"  v={node.bbUserId}      m />
          <TRow l="VLAN"        v={String(node.vlan ?? '—')} m />
          <TRow l="OLT Port"    v={node.portNum !== undefined ? `Port ${node.portNum}` : '—'} />
          <TRow l="Plan"        v={node.subscriptionPlan} />
          <TRow l="FMC"         v={`₹${node.fmc ?? '—'}/month`} c="text-emerald-600 font-bold" />
          <TRow l="Mobile"      v={node.mobile} m />
          <TRow l="RX Power"    v={powerLabel(node.rxPower)} c={powerColor(node.rxPower)} />
          <TRow l="Path Loss"   v={`${node.pathLoss?.toFixed(2)} dB`} />
          {isWeak && (
            <div className="mt-2 flex gap-1.5 px-2 py-1.5 rounded-lg" style={{background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.15)'}}>
              <AlertTriangle size={10} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-red-600 text-[10px] font-medium">Below minimum ({ONT_MIN_RX} dBm) — check fiber splices</p>
            </div>
          )}
        </>)}
      </div>
    </div>
  )
}

function TRow({ l, v, m = false, c }: { l: string; v?: string | number; m?: boolean; c?: string }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <span className="text-slate-500 shrink-0">{l}</span>
      <span className={`${m ? 'font-mono' : 'font-semibold'} text-right truncate max-w-[160px] ${c ?? 'text-slate-800'}`}>
        {v ?? '—'}
      </span>
    </div>
  )
}

// ─── Properties Panel ─────────────────────────────────────────────────────────

function PropertiesPanel({ node, onUpdate, onClose }: {
  node: TopoNode
  onUpdate: (id: string, patch: Partial<TopoNode>) => void
  onClose: () => void
}) {
  const [txPower, setTxPower] = useState(String(node.txPower ?? 3.5))
  const [ratio, setRatio]     = useState(String(node.ratio ?? 8))

  // Update local state when selected node changes
  useEffect(() => { setTxPower(String(node.txPower ?? 3.5)) }, [node.txPower])
  useEffect(() => { setRatio(String(node.ratio ?? 8)) }, [node.ratio])

  return (
    <div
      className="absolute right-4 top-4 bottom-4 w-72 rounded-3xl overflow-hidden flex flex-col z-40 border border-slate-200/80 shadow-2xl"
      style={{ background:'rgba(255,255,255,0.98)', backdropFilter:'blur(24px)',
        boxShadow:'0 20px 48px rgba(0,0,0,0.1)' }}
      /* stop pointer events from falling through to SVG */
      onPointerDown={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0"
        style={{ background:'linear-gradient(135deg,rgba(59,130,246,0.06),rgba(6,182,212,0.03))' }}>
        <div className="flex items-center gap-2.5">
          {node.type === 'olt'      && <Server   size={14} className="text-blue-600" />}
          {node.type === 'splitter' && <Layers   size={14} className="text-purple-600" />}
          {node.type === 'building' && <Building2 size={14} className="text-cyan-600" />}
          {node.type === 'ont'      && <Wifi     size={14} className="text-emerald-600" />}
          <div>
            <p className="text-slate-950 font-black text-sm leading-none">
              {node.type === 'ont' ? (node.customerName?.split(' ').slice(0,2).join(' ') ?? 'ONT') : node.label.replace(/\n/g,' ')}
            </p>
            <p className="text-slate-500 text-[10px] mt-0.5 font-bold uppercase tracking-wider">{node.type}</p>
          </div>
        </div>
        <button onClick={onClose}
          className="w-7 h-7 rounded-xl flex items-center justify-center hover:bg-slate-100 transition-all shrink-0">
          <X size={13} className="text-slate-400" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto scrollbar-hide p-4 space-y-3">

        {/* Signal gauge */}
        {node.rxPower !== undefined && (
          <div className="rounded-2xl p-3 space-y-2"
            style={{ background:'rgba(0,0,0,0.02)', border:'1px solid rgba(0,0,0,0.04)' }}>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Optical Signal</p>
            <div className="flex items-end justify-between">
              <span className="text-slate-500 text-xs font-medium">RX Power</span>
              <span className="font-black text-xl leading-none" style={{ color: powerColor(node.rxPower) }}>
                {powerLabel(node.rxPower)}
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width:`${Math.max(2, Math.min(100, ((node.rxPower + 40) / 40) * 100))}%`,
                  background: powerColor(node.rxPower) }} />
            </div>
            <div className="flex justify-between text-[9px] text-slate-400 font-mono">
              <span>–40 dBm</span><span>0 dBm</span>
            </div>
            <IRow l="Path Loss" v={`${node.pathLoss?.toFixed(2)} dB`} />
          </div>
        )}

        {/* OLT identity */}
        {node.type === 'olt' && (
          <InfoSection title="OLT Details">
            <IRow l="IP Address" v={node.oltIp} m />
            <IRow l="Standard"   v="GPON ITU-T G.984" />
            <IRow l="Wavelength" v="1490 nm downstream" />
          </InfoSection>
        )}

        {/* Splitter identity */}
        {node.type === 'splitter' && (
          <InfoSection title="Splitter">
            <IRow l="Port"       v={node.port !== undefined ? `Port ${node.port}` : '—'} />
            <IRow l="Split"      v={`1 : ${node.ratio}`} />
            <IRow l="Loss"       v={`${splitterLoss(node.ratio!).toFixed(1)} dB`} />
          </InfoSection>
        )}

        {/* Building identity */}
        {node.type === 'building' && (
          <InfoSection title="Building">
            <IRow l="Name"    v={node.building} />
            <IRow l="Port"    v={node.port !== undefined ? `Port ${node.port}` : '—'} />
            <IRow l="Tenants" v={String(node._custCount ?? 0)} />
          </InfoSection>
        )}

        {/* ONT identity + subscription */}
        {node.type === 'ont' && (<>
          <InfoSection title="Customer">
            <IRow l="Name"       v={node.customerName} />
            <IRow l="Service No" v={node.serviceNumber} m />
            <IRow l="BB User ID" v={node.bbUserId}      m />
            <IRow l="Mobile"     v={node.mobile}        m />
          </InfoSection>
          <InfoSection title="Subscription">
            <IRow l="Plan" v={node.subscriptionPlan} />
            <IRow l="FMC"  v={`₹${node.fmc ?? '—'}/month`} />
            <IRow l="VLAN" v={String(node.vlan ?? '—')} m />
            <IRow l="Port" v={node.portNum !== undefined ? `Port ${node.portNum}` : '—'} />
          </InfoSection>
        </>)}

        {/* Editable: OLT TX Power */}
        {node.type === 'olt' && (
          <div className="rounded-2xl p-3 space-y-3"
            style={{ background:'rgba(59,130,246,0.04)', border:'1px solid rgba(59,130,246,0.15)' }}>
            <p className="text-[9px] font-black uppercase tracking-widest text-blue-600">Edit TX Power</p>
            <div>
              <label className="text-slate-600 text-[11px] mb-1.5 block font-semibold">Transmit Power (dBm)</label>
              <input type="number" value={txPower} onChange={e => setTxPower(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 text-sm outline-none focus:border-blue-500 font-mono transition-colors"
                step="0.5" min="-10" max="10" />
            </div>
            <button onClick={() => onUpdate(node.id, { txPower: parseFloat(txPower) || node.txPower! })}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-white text-xs font-black transition-all active:scale-95 shadow-md hover:shadow-lg"
              style={{ background:'linear-gradient(135deg,#3b82f6,#2563eb)' }}>
              <Save size={12} /> Apply & Recalculate
            </button>
          </div>
        )}

        {/* Editable: Splitter ratio */}
        {node.type === 'splitter' && (
          <div className="rounded-2xl p-3 space-y-3"
            style={{ background:'rgba(139,92,246,0.04)', border:'1px solid rgba(139,92,246,0.15)' }}>
            <p className="text-[9px] font-black uppercase tracking-widest text-purple-600">Edit Split Ratio</p>
            <div>
              <label className="text-slate-600 text-[11px] mb-1.5 block font-semibold">Ratio (1:N)</label>
              <select value={ratio} onChange={e => setRatio(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 text-sm outline-none focus:border-purple-500 transition-colors">
                {[2,4,8,16,32,64].map(r => (
                  <option key={r} value={r} style={{ background:'white', color: '#1e293b' }}>
                    1:{r} — {splitterLoss(r).toFixed(1)} dB loss
                  </option>
                ))}
              </select>
            </div>
            <button onClick={() => onUpdate(node.id, { ratio: parseInt(ratio) || node.ratio! })}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-white text-xs font-black transition-all active:scale-95 shadow-md hover:shadow-lg"
              style={{ background:'linear-gradient(135deg,#8b5cf6,#7c3aed)' }}>
              <Save size={12} /> Apply & Recalculate
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function InfoSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-3 space-y-1.5"
      style={{ background:'rgba(0,0,0,0.02)', border:'1px solid rgba(0,0,0,0.04)' }}>
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">{title}</p>
      {children}
    </div>
  )
}

function IRow({ l, v, m = false }: { l: string; v?: string | number; m?: boolean }) {
  return (
    <div className="flex justify-between items-start gap-2">
      <span className="text-slate-500 text-[11px] shrink-0">{l}</span>
      <span className={`text-slate-700 text-[11px] text-right max-w-[170px] ${m ? 'font-mono' : 'font-semibold'}`}>{v ?? '—'}</span>
    </div>
  )
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({
  ports, buildings, filterPort, filterBuilding, onPort, onBuilding, onReset
}: {
  ports: number[]
  buildings: string[]
  filterPort: number | null
  filterBuilding: string | null
  onPort: (p: number | null) => void
  onBuilding: (b: string | null) => void
  onReset: () => void
}) {
  const hasFilter = filterPort !== null || filterBuilding !== null
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b shrink-0 overflow-x-auto scrollbar-hide"
      style={{ borderColor:'rgba(0,0,0,0.06)', background:'rgba(255,255,255,0.45)', backdropFilter: 'blur(8px)' }}>
      <div className="flex items-center gap-1.5 shrink-0">
        <Filter size={11} className="text-slate-600" />
        <span className="text-slate-600 text-[10px] font-bold uppercase tracking-wider">Filter</span>
      </div>
      <div className="w-px h-4 bg-slate-200 mx-1 shrink-0" />

      {/* Port pills */}
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-slate-500 text-[10px] font-semibold">Port</span>
        <Pill active={filterPort === null} onClick={() => onPort(null)} label="All" />
        {ports.map(p => (
          <Pill key={p} active={filterPort === p} onClick={() => onPort(filterPort === p ? null : p)} label={`${p}`} />
        ))}
      </div>

      <div className="w-px h-4 bg-slate-200 mx-1 shrink-0" />

      {/* Building dropdown */}
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-slate-500 text-[10px] font-semibold">Building</span>
        <select
          value={filterBuilding ?? ''}
          onChange={e => onBuilding(e.target.value || null)}
          className="text-[11px] font-semibold rounded-xl px-2 py-1 outline-none transition-all"
          style={{
            background: filterBuilding ? 'rgba(6,182,212,0.08)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${filterBuilding ? 'rgba(6,182,212,0.25)' : 'rgba(0,0,0,0.06)'}`,
            color: filterBuilding ? '#0e7490' : '#64748b'
          }}>
          <option value="" style={{ background:'white', color: '#475569' }}>All Buildings</option>
          {buildings.map(b => (
            <option key={b} value={b} style={{ background:'white', color: '#475569' }}>{b}</option>
          ))}
        </select>
      </div>

      {hasFilter && (
        <button onClick={onReset}
          className="flex items-center gap-1 px-2 py-1 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all text-[10px] shrink-0 ml-1">
          <RotateCcw size={10} /> Clear
        </button>
      )}

      <div className="flex-1" />
      <span className="text-slate-400 text-[10px] shrink-0 font-medium">
        Drag canvas to pan · Scroll to zoom · Click node · Drag node to reposition
      </span>
    </div>
  )
}

function Pill({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button onClick={onClick}
      className="px-2.5 py-1 rounded-xl text-[10px] font-bold transition-all"
      style={{
        background: active ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.03)',
        border: `1px solid ${active ? 'rgba(59,130,246,0.25)' : 'rgba(0,0,0,0.06)'}`,
        color: active ? '#1d4ed8' : '#64748b'
      }}>
      {label}
    </button>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const NetworkTopologyPage: React.FC = () => {
  // Raw data
  const rawCustomers = useMemo(
    () => (Array.isArray(customersJson) ? (customersJson as unknown) as CustomerRecord[] : []),
    []
  )

  // OLT TX power
  const [oltTxPower, setOltTxPower] = useState(3.5)

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Toggle fullscreen using native browser API
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen()
        .catch(err => {
          console.warn('Native fullscreen failed:', err)
          setIsFullscreen(true)
        })
    } else {
      document.exitFullscreen()
        .catch(err => {
          console.warn('Native exit fullscreen failed:', err)
          setIsFullscreen(false)
        })
    }
  }, [])

  // Sync isFullscreen with native document state and Escape key
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {})
        }
        setIsFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Calibration state
  const [hasUnsaved, setHasUnsaved] = useState(false)
  const [lastSaved, setLastSaved]   = useState<string | null>(null)

  // Full topology
  const [nodes, setNodes] = useState<TopoNode[]>([])
  const [edges, setEdges] = useState<TopoEdge[]>([])

  // Build topology once from customer data, then restore any saved calibration
  useEffect(() => {
    const { nodes: n, edges: e } = buildTopology(rawCustomers, 3.5)
    const cal = loadCalibration()
    const txPower = cal?.oltTxPower ?? 3.5
    const calibrated = cal ? applyCalibration(n, cal) : n
    const computed   = recalculatePowers(calibrated, e, txPower)
    setOltTxPower(txPower)
    setNodes(computed)
    setEdges(e)
    if (cal) { setLastSaved(cal.savedLabel); setHasUnsaved(false) }
  }, [rawCustomers])

  // Derived metadata for filters
  const allPorts     = useMemo(() => [...new Set(nodes.filter(n => n.type === 'splitter' && n.port !== undefined).map(n => n.port!))].sort(), [nodes])
  const allBuildings = useMemo(() => [...new Set(nodes.filter(n => n.type === 'building').map(n => n.building!))].sort(), [nodes])
  const nodeMap      = useMemo(() => { const m: Record<string, TopoNode> = {}; nodes.forEach(n => { m[n.id] = n }); return m }, [nodes])

  // Filters
  const [filterPort, setFilterPort]         = useState<number | null>(null)
  const [filterBuilding, setFilterBuilding] = useState<string | null>(null)

  // Filtered subgraph
  const { visibleNodes, visibleEdges } = useMemo(() => {
    const noFilter = !filterPort && !filterBuilding
    if (noFilter) return { visibleNodes: nodes, visibleEdges: edges }

    const visibleIds = new Set<string>()
    // Always show OLT
    nodes.forEach(n => { if (n.type === 'olt') visibleIds.add(n.id) })
    // Splitters matching port filter (or all if no port filter)
    nodes.forEach(n => {
      if (n.type === 'splitter') {
        if (!filterPort || n.port === filterPort) visibleIds.add(n.id)
      }
    })
    // Buildings
    nodes.forEach(n => {
      if (n.type === 'building') {
        const portOk     = !filterPort    || n.port === filterPort
        const buildingOk = !filterBuilding || n.building === filterBuilding
        if (portOk && buildingOk) visibleIds.add(n.id)
      }
    })
    // ONTs under visible buildings
    nodes.forEach(n => {
      if (n.type === 'ont') {
        const portOk     = !filterPort    || n.portNum === filterPort
        const buildingOk = !filterBuilding || n.building === filterBuilding
        if (portOk && buildingOk) visibleIds.add(n.id)
      }
    })

    const vn = nodes.filter(n => visibleIds.has(n.id))
    const ve = edges.filter(e => visibleIds.has(e.from) && visibleIds.has(e.to))
    return { visibleNodes: vn, visibleEdges: ve }
  }, [nodes, edges, filterPort, filterBuilding])

  // Auto-fit when filter changes
  const svgRef        = useRef<SVGSVGElement>(null)
  const containerRef  = useRef<HTMLDivElement>(null)
  const [transform, setTransform] = useState({ x: 400, y: 80, scale: 0.52 })

  const fitToNodes = useCallback((targetNodes: TopoNode[]) => {
    if (!targetNodes.length || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const xs = targetNodes.map(n => n.x)
    const ys = targetNodes.map(n => n.y)
    const minX = Math.min(...xs) - 80
    const maxX = Math.max(...xs) + 80
    const minY = Math.min(...ys) - 60
    const maxY = Math.max(...ys) + 80
    const scaleX = rect.width  / (maxX - minX)
    const scaleY = rect.height / (maxY - minY)
    const newScale = Math.min(1.2, Math.max(0.15, Math.min(scaleX, scaleY) * 0.88))
    const cx = (minX + maxX) / 2
    const cy = (minY + maxY) / 2
    setTransform({
      scale: newScale,
      x: rect.width  / 2 - cx * newScale,
      y: rect.height / 2 - cy * newScale
    })
  }, [])

  useEffect(() => {
    if (visibleNodes.length) fitToNodes(visibleNodes)
  }, [filterPort, filterBuilding]) // eslint-disable-line

  // ── Interaction state — all in refs to avoid setState on every frame ──────

  // Drag state
  const drag = useRef<{
    id: string | null
    startClientX: number; startClientY: number
    nodeStartX:   number; nodeStartY:   number
    hasMoved:     boolean
  }>({ id: null, startClientX: 0, startClientY: 0, nodeStartX: 0, nodeStartY: 0, hasMoved: false })

  // Pan state
  const pan = useRef<{ active: boolean; startX: number; startY: number }>({ active: false, startX: 0, startY: 0 })

  // Mouse position for tooltip
  const mousePos = useRef({ x: 0, y: 0 })
  const tooltipDivRef = useRef<HTMLDivElement>(null)

  // Selected / hovered state
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [hoveredId,  setHoveredId]  = useState<string | null>(null)
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null)
  const [tooltipNode, setTooltipNode] = useState<TopoNode | null>(null)

  // Level-of-detail: hide ONTs when zoomed out
  const showOnts = transform.scale >= 0.32

  // Zoom
  const [showLegend, setShowLegend] = useState(false)

  const selectedNode = selectedId ? nodeMap[selectedId] : null

  // ── Stable per-node callbacks (keyed by id) so memo works ──
  const nodeCallbacks = useMemo(() => {
    const cbs: Record<string, {
      onPointerDown:  (e: React.PointerEvent) => void
      onPointerEnter: (e: React.PointerEvent) => void
      onPointerLeave: (e: React.PointerEvent) => void
    }> = {}
    visibleNodes.forEach(node => {
      cbs[node.id] = {
        onPointerDown: (e: React.PointerEvent) => {
          e.stopPropagation()
          const rect = svgRef.current?.getBoundingClientRect()
          if (!rect) return
          drag.current = {
            id: node.id,
            startClientX: e.clientX, startClientY: e.clientY,
            nodeStartX: node.x, nodeStartY: node.y,
            hasMoved: false
          }
          ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
        },
        onPointerEnter: (_: React.PointerEvent) => {
          setHoveredId(node.id)
          setTooltipNode(node)
        },
        onPointerLeave: (_: React.PointerEvent) => {
          setHoveredId(null)
          setTooltipNode(null)
        },
      }
    })
    return cbs
  }, [visibleNodes]) // eslint-disable-line

  // ── Container mouse tracking (tooltip reposition without setState) ──
  const onContainerMouseMove = useCallback((e: React.MouseEvent) => {
    mousePos.current = { x: e.clientX, y: e.clientY }
    if (tooltipDivRef.current) {
      const vw = window.innerWidth
      const x = e.clientX + 18
      const y = e.clientY - 12
      // Flip left if near right edge
      tooltipDivRef.current.style.left = x + 280 > vw ? `${e.clientX - 298}px` : `${x}px`
      tooltipDivRef.current.style.top  = `${y}px`
    }
  }, [])

  // ── SVG pointer events ─────────────────────────────────────────────────────

  const onSvgPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    // Only start pan if nothing was dragging a node
    if (drag.current.id) return
    pan.current = { active: true, startX: e.clientX - transform.x, startY: e.clientY - transform.y }
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
  }, [transform])

  const onSvgPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    // ── Node drag ──
    if (drag.current.id) {
      const dx = e.clientX - drag.current.startClientX
      const dy = e.clientY - drag.current.startClientY
      if (!drag.current.hasMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        drag.current.hasMoved = true
      }
      if (drag.current.hasMoved) {
        const id = drag.current.id
        setNodes(prev => prev.map(n => n.id === id
          ? { ...n,
              x: drag.current.nodeStartX + dx / transform.scale,
              y: drag.current.nodeStartY + dy / transform.scale }
          : n
        ))
        setHasUnsaved(true)
      }
      return
    }
    // ── Pan ──
    if (pan.current.active) {
      setTransform(t => ({ ...t, x: e.clientX - pan.current.startX, y: e.clientY - pan.current.startY }))
    }
  }, [transform.scale])

  const onSvgPointerUp = useCallback((_e: React.PointerEvent<SVGSVGElement>) => {
    // If node wasn't dragged → it's a click → toggle selection
    if (drag.current.id && !drag.current.hasMoved) {
      const id = drag.current.id
      setSelectedId(prev => prev === id ? null : id)
    }
    drag.current.id = null
    pan.current.active = false
  }, [])

  // Native non-passive wheel event listener to prevent window scroll while zooming
  useEffect(() => {
    const svgEl = svgRef.current
    if (!svgEl) return

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY < 0 ? 1.13 : 0.88
      setTransform(t => {
        const newScale = Math.min(3, Math.max(0.12, t.scale * delta))
        const rect = svgEl.getBoundingClientRect()
        if (!rect) return { ...t, scale: newScale }
        const cx = e.clientX - rect.left
        const cy = e.clientY - rect.top
        return {
          scale: newScale,
          x: cx - (cx - t.x) * (newScale / t.scale),
          y: cy - (cy - t.y) * (newScale / t.scale)
        }
      })
    }

    svgEl.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      svgEl.removeEventListener('wheel', handleWheel)
    }
  }, [])

  // ── Counts ──
  const totalOnts  = nodes.filter(n => n.type === 'ont').length
  const weakSignal = nodes.filter(n => n.type === 'ont' && n.rxPower !== undefined && n.rxPower < ONT_MIN_RX).length
  const warnSignal = nodes.filter(n => n.type === 'ont' && n.rxPower !== undefined && n.rxPower >= ONT_MIN_RX && n.rxPower < ONT_WARN_RX).length

  // ── Property update + full tree recalculation ──
  const handleUpdate = useCallback((id: string, patch: Partial<TopoNode>) => {
    const newTxPower = patch.txPower ?? oltTxPower
    setNodes(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, ...patch } : n)
      return recalculatePowers(updated, edges, newTxPower)
    })
    if (patch.txPower !== undefined) setOltTxPower(patch.txPower)
    setHasUnsaved(true)
    setSelectedId(null)
  }, [edges, oltTxPower])

  // ── Recalculate without changing anything ──
  const handleRecalculate = useCallback(() => {
    setNodes(prev => recalculatePowers(prev, edges, oltTxPower))
    setHasUnsaved(true)
  }, [edges, oltTxPower])

  // ── Save calibration ──
  const handleSave = useCallback(() => {
    const label = saveCalibration(oltTxPower, nodes)
    setLastSaved(label)
    setHasUnsaved(false)
  }, [oltTxPower, nodes])

  // ── Reset to factory defaults ──
  const handleReset = useCallback(() => {
    if (!window.confirm('Reset all calibration to factory defaults? This cannot be undone.')) return
    localStorage.removeItem(CAL_KEY)
    const { nodes: n, edges: e } = buildTopology(rawCustomers, 3.5)
    const computed = recalculatePowers(n, e, 3.5)
    setOltTxPower(3.5)
    setNodes(computed)
    setEdges(e)
    setHasUnsaved(false)
    setLastSaved(null)
  }, [rawCustomers])

  // Render nodes to show (LOD)
  const renderNodes = useMemo(
    () => showOnts ? visibleNodes : visibleNodes.filter(n => n.type !== 'ont'),
    [visibleNodes, showOnts]
  )
  const renderEdges = useMemo(
    () => showOnts ? visibleEdges : visibleEdges.filter(e => {
      const to = nodeMap[e.to]
      return to && to.type !== 'ont'
    }),
    [visibleEdges, showOnts, nodeMap]
  )

  // Only show tooltip when a node is hovered and not selected
  const showTooltip = !!tooltipNode && !selectedNode

  return (
    <div
      ref={containerRef}
      className={
        isFullscreen
          ? "fixed inset-0 z-[150] flex flex-col w-screen h-screen overflow-hidden animate-page-enter select-none bg-slate-50"
          : "flex flex-col rounded-3xl overflow-hidden animate-page-enter relative select-none"
      }
      style={{
        height: isFullscreen ? '100vh' : 'calc(100vh - 80px)',
        background: 'linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)'
      }}
      onMouseMove={onContainerMouseMove}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b shrink-0"
        style={{ borderColor:'rgba(0,0,0,0.06)', background:'rgba(255,255,255,0.7)', backdropFilter:'blur(12px)' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background:'linear-gradient(135deg,rgba(59,130,246,0.1),rgba(6,182,212,0.06))', border:'1px solid rgba(59,130,246,0.15)' }}>
            <Cable size={15} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-slate-900 font-black text-sm">GPON Fiber Network Topology</h1>
            <p className="text-slate-500 text-[10px] font-medium">OLT · Splitters · Buildings · ONT endpoints</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip icon={<Database size={10} />} label={`${totalOnts} ONTs`}
            color="text-blue-700" bg="rgba(59,130,246,0.08)" border="rgba(59,130,246,0.15)" />
          {warnSignal > 0 && <StatusChip icon={<AlertTriangle size={10} />} label={`${warnSignal} Warn`}
            color="text-amber-700" bg="rgba(245,158,11,0.08)" border="rgba(245,158,11,0.18)" />}
          {weakSignal > 0 && <StatusChip icon={<AlertTriangle size={10} />} label={`${weakSignal} Weak`}
            color="text-red-700" bg="rgba(239,68,68,0.08)" border="rgba(239,68,68,0.15)" />}
          {weakSignal === 0 && warnSignal === 0 && <StatusChip icon={<CheckCircle size={10} />} label="All OK"
            color="text-emerald-700" bg="rgba(16,185,129,0.08)" border="rgba(16,185,129,0.15)" />}

          <div className="w-px h-5 bg-slate-200 mx-1" />

          {/* OLT TX Power inline edit + Recalculate */}
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl animate-spring"
            style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.18)' }}>
            <Zap size={10} className="text-blue-500 shrink-0" />
            <span className="text-blue-700 text-[10px] font-bold shrink-0">OLT TX</span>
            <input type="number" value={oltTxPower}
              onChange={e => { setOltTxPower(parseFloat(e.target.value) || 3.5); setHasUnsaved(true) }}
              onKeyDown={e => { if (e.key === 'Enter') handleRecalculate() }}
              className="w-12 bg-transparent text-slate-800 text-[11px] font-mono font-bold outline-none"
              step="0.5" min="-5" max="10" />
            <span className="text-blue-600/60 text-[10px] shrink-0 font-bold">dBm</span>
          </div>

          {/* Recalculate */}
          <button onClick={handleRecalculate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all active:scale-95"
            style={{ background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.18)', color:'#4f46e5' }}
            title="Recalculate all optical power values">
            <RotateCcw size={10} /> Recalculate
          </button>

          <div className="w-px h-5 bg-slate-200 mx-0.5" />

          {/* Save button + unsaved badge */}
          <div className="flex items-center gap-1.5">
            {hasUnsaved && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[9px] font-bold"
                style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', color:'#b45309' }}>
                ● Unsaved
              </div>
            )}
            <button onClick={handleSave}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all active:scale-95 shadow-sm"
              style={{
                background: hasUnsaved
                  ? 'linear-gradient(135deg,#10b981,#059669)'
                  : 'rgba(0,0,0,0.03)',
                border: `1px solid ${hasUnsaved ? 'transparent' : 'rgba(0,0,0,0.07)'}`,
                color: hasUnsaved ? 'white' : '#64748b',
                boxShadow: hasUnsaved ? '0 2px 8px rgba(16,185,129,0.2)' : 'none'
              }}
              title="Save current calibration (TX power, splitter ratios, node positions)">
              <Save size={10} /> Save
            </button>
            {lastSaved && !hasUnsaved && (
              <span className="text-[9px] text-slate-500 font-semibold">Saved {lastSaved}</span>
            )}
            <button onClick={handleReset}
              className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-all"
              title="Reset all calibration to factory defaults">
              <RotateCcw size={9} className="text-slate-400 hover:text-red-500" />
            </button>
          </div>

          {/* Zoom */}
          <div className="flex items-center gap-0.5">
            <button onClick={() => setTransform(t => ({ ...t, scale: Math.min(3, t.scale * 1.2) }))}
              className="w-7 h-7 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-all">
              <ZoomIn size={12} className="text-slate-500" />
            </button>
            <span className="text-slate-500 text-[10px] w-9 text-center font-bold">{Math.round(transform.scale * 100)}%</span>
            <button onClick={() => setTransform(t => ({ ...t, scale: Math.max(0.12, t.scale * 0.8) }))}
              className="w-7 h-7 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-all">
              <ZoomOut size={12} className="text-slate-500" />
            </button>
          </div>

          <button onClick={() => fitToNodes(visibleNodes)}
            className="w-7 h-7 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-all" title="Fit">
            <Maximize2 size={12} className="text-slate-500" />
          </button>
          <button onClick={toggleFullscreen}
            className={`w-7 h-7 flex items-center justify-center rounded-xl transition-all ${
              isFullscreen ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-100 text-slate-500'
            }`}
            title={isFullscreen ? "Exit Fullscreen (Esc)" : "Fullscreen Mode"}>
            {isFullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </button>
          <button onClick={() => setShowLegend(s => !s)}
            className="w-7 h-7 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-all" title="Legend">
            <Info size={12} className="text-slate-500" />
          </button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <FilterBar
        ports={allPorts} buildings={allBuildings}
        filterPort={filterPort} filterBuilding={filterBuilding}
        onPort={p => { setFilterPort(p); setSelectedId(null) }}
        onBuilding={b => { setFilterBuilding(b); setSelectedId(null) }}
        onReset={() => { setFilterPort(null); setFilterBuilding(null); setSelectedId(null) }}
      />

      {/* ── LOD hint ── */}
      {!showOnts && (
        <div className="absolute top-28 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] text-slate-600 font-semibold shadow-sm"
            style={{ background:'rgba(255,255,255,0.9)', border:'1px solid rgba(0,0,0,0.06)' }}>
            <Wifi size={10} />
            Zoom in to see individual ONT endpoints
          </div>
        </div>
      )}

      {/* ── SVG Canvas ── */}
      <div className="flex-1 relative overflow-hidden">
        <svg
          ref={svgRef}
          width="100%" height="100%"
          style={{ cursor: drag.current.id ? 'grabbing' : pan.current.active ? 'grabbing' : 'grab' }}
          onPointerDown={onSvgPointerDown}
          onPointerMove={onSvgPointerMove}
          onPointerUp={onSvgPointerUp}
          onPointerLeave={onSvgPointerUp}
        >
          <defs>
            <pattern id="dg" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.7" fill="rgba(0,0,0,0.06)" />
            </pattern>
            <linearGradient id="oltGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1e3a8a" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
            <linearGradient id="splitterGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#581c87" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
            <linearGradient id="buildingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0f766e" />
              <stop offset="100%" stopColor="#06b6d4" />
            </linearGradient>
            <linearGradient id="ontGoodGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#047857" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
            <linearGradient id="ontWarnGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#b45309" />
              <stop offset="100%" stopColor="#f59e0b" />
            </linearGradient>
            <linearGradient id="ontWeakGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#b91c1c" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
            <linearGradient id="ontOfflineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#475569" />
              <stop offset="100%" stopColor="#64748b" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#dg)" />

          <g transform={`translate(${transform.x},${transform.y}) scale(${transform.scale})`}>
            {/* Edges first */}
            {renderEdges.map(edge => (
              <g key={edge.id}
                onMouseEnter={() => setHoveredEdgeId(edge.id)}
                onMouseLeave={() => setHoveredEdgeId(null)}>
                <TopologyEdge
                  edge={edge}
                  fromNode={nodeMap[edge.from]}
                  toNode={nodeMap[edge.to]}
                  hovered={hoveredEdgeId === edge.id}
                />
              </g>
            ))}

            {/* Nodes */}
            {renderNodes.map(node => {
              const cbs = nodeCallbacks[node.id]
              if (!cbs) return null
              return (
                <TopologyNode
                  key={node.id}
                  node={node}
                  selected={selectedId === node.id}
                  hovered={hoveredId === node.id}
                  onPointerDown={cbs.onPointerDown}
                  onPointerEnter={cbs.onPointerEnter}
                  onPointerLeave={cbs.onPointerLeave}
                />
              )
            })}
          </g>
        </svg>

        {/* Tooltip — pointer-events-none, position set via DOM ref */}
        <div
          ref={tooltipDivRef}
          className="fixed z-[300] pointer-events-none transition-opacity duration-150"
          style={{
            opacity: showTooltip ? 1 : 0,
            left: mousePos.current.x + 18,
            top:  mousePos.current.y - 12,
          }}
        >
          {tooltipNode && <NodeTooltip node={tooltipNode} />}
        </div>

        {/* Properties Panel */}
        {selectedNode && (
          <PropertiesPanel
            node={selectedNode}
            onUpdate={handleUpdate}
            onClose={() => setSelectedId(null)}
          />
        )}

        {/* Legend */}
        {showLegend && (
          <div className="absolute left-4 bottom-4 rounded-2xl overflow-hidden z-30 border border-slate-200"
            style={{ background:'rgba(255,255,255,0.98)', backdropFilter:'blur(16px)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
              <p className="text-slate-800 font-black text-xs">Legend</p>
              <button onClick={() => setShowLegend(false)} className="hover:bg-slate-100 rounded-lg p-0.5 transition-all">
                <X size={11} className="text-slate-400" />
              </button>
            </div>
            <div className="p-3 space-y-1.5 text-xs">
              {[
                { c:'#059669', t:`Good signal (≥ ${ONT_WARN_RX} dBm)` },
                { c:'#d97706', t:`Low signal (${ONT_MIN_RX} to ${ONT_WARN_RX} dBm)` },
                { c:'#dc2626', t:`Weak signal (< ${ONT_MIN_RX} dBm)` },
              ].map(i => (
                <div key={i.t} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background:i.c }} />
                  <span className="text-slate-600 font-medium">{i.t}</span>
                </div>
              ))}
              <div className="pt-1.5 border-t border-slate-100 space-y-1.5 text-slate-500 text-[11px] font-medium">
                <div className="flex items-center gap-2"><Server   size={10} className="text-blue-600"    /><span>OLT — Optical Line Terminal</span></div>
                <div className="flex items-center gap-2"><Layers   size={10} className="text-purple-600"  /><span>1:N Passive Optical Splitter</span></div>
                <div className="flex items-center gap-2"><Building2 size={10} className="text-cyan-600"   /><span>Building / Distribution point</span></div>
                <div className="flex items-center gap-2"><Wifi     size={10} className="text-emerald-600" /><span>ONT — Customer endpoint</span></div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 text-slate-400 text-[10px] space-y-0.5 font-medium">
                <p>Left side bar = signal strength</p>
                <p>Pulsing dot = signal alert</p>
              </div>
            </div>
          </div>
        )}

        {/* Coordinate display */}
        <div className="absolute bottom-3 right-3 px-2.5 py-1.5 rounded-xl text-[10px] font-mono font-semibold"
          style={{ background:'rgba(255,255,255,0.8)', border:'1px solid rgba(0,0,0,0.06)', color:'#64748b' }}>
          {Math.round(transform.scale * 100)}% · x:{Math.round(-transform.x/transform.scale)} y:{Math.round(-transform.y/transform.scale)}
        </div>
      </div>
    </div>
  )
}

function StatusChip({ icon, label, color, bg, border }: { icon: React.ReactNode; label: string; color: string; bg: string; border: string }) {
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[10px] font-bold ${color}`}
      style={{ background:bg, border:`1px solid ${border}` }}>
      {icon}{label}
    </div>
  )
}

export default NetworkTopologyPage
